import type { GuestExperience } from '../lib/experience/guestExperience';
import type { GuestWorkflowSection } from '../app/guestWorkflows';

export function WorkflowStatus({
  status,
}: {
  status: GuestWorkflowSection['status'];
}) {
  return (
    <span className={`workflow-status workflow-status--${status}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function formatToken(value: string) {
  return value.replaceAll('_', ' ');
}

export function getSectionDisplayLabel(
  section: GuestExperience['guestWebsite']['sections'][number],
) {
  return (
    section.anchor_label ??
    (typeof section.content_payload.title === 'string'
      ? section.content_payload.title
      : section.section_key)
  );
}

export function getSectionSummary(
  section: GuestExperience['guestWebsite']['sections'][number],
) {
  const summaryCandidates = [
    section.content_payload.subtitle,
    section.content_payload.summary,
    section.content_payload.description,
    section.content_payload.body,
  ];

  const summary = summaryCandidates.find(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate.trim().length > 0,
  );

  if (summary) {
    return summary;
  }

  if (section.linked_event_id) {
    return `Linked to event ${section.linked_event_id} and shown with ${formatToken(section.visibility_level)} visibility.`;
  }

  return `${formatToken(section.type)} content with ${formatToken(section.visibility_level)} visibility.`;
}

function truncateSummary(value: string, maxLength = 180) {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}...`;
}

export function getSectionTargetPath(
  section: GuestExperience['guestWebsite']['sections'][number],
) {
  if (section.linked_module_key === 'rsvp' || section.type === 'rsvp') {
    return '/rsvp';
  }
  if (section.linked_module_key === 'forms' || section.type === 'forms') {
    return '/forms';
  }
  if (section.linked_module_key === 'registry' || section.type === 'registry') {
    return '/registry';
  }
  if (
    section.linked_module_key === 'gallery' ||
    section.type === 'gallery_preview'
  ) {
    return '/gallery';
  }
  if (section.type === 'event_list' || section.type === 'event_highlights') {
    return '/events';
  }

  return '/';
}

export function buildSectionCluster(input: {
  title: string;
  description: string;
  ctaLabel: string;
  fallbackPath: string;
  types: GuestExperience['guestWebsite']['sections'][number]['type'][];
  sections: GuestExperience['guestWebsite']['sections'];
}) {
  const matchingSections = input.sections.filter(
    (section) => section.enabled && input.types.includes(section.type),
  );

  if (matchingSections.length === 0) {
    return null;
  }

  return {
    title: input.title,
    description: input.description,
    ctaLabel: input.ctaLabel,
    fallbackPath: input.fallbackPath,
    items: matchingSections.map((section) => ({
      id: section.id,
      label: getSectionDisplayLabel(section),
      summary: truncateSummary(getSectionSummary(section)),
      targetPath: getSectionTargetPath(section),
    })),
  };
}
